
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Bot, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestedCharts?: string[];
}

interface ChatInterfaceProps {
  onChartRequest: (chartType: string, query: string) => void;
}

export function ChatInterface({ onChartRequest }: ChatInterfaceProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'assistant',
      content: 'Hello! I can help you analyze your loyalty program performance. Try asking me something like "Show me this month\'s top 5 underperforming campaigns" or "What\'s our current program ROI?"',
      timestamp: new Date(),
      suggestedCharts: []
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Simulate AI response with chart suggestions
    setTimeout(() => {
      const response = generateResponse(inputValue);
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.content,
        timestamp: new Date(),
        suggestedCharts: response.charts
      };

      setMessages(prev => [...prev, assistantMessage]);
      setIsLoading(false);

      // Auto-generate first suggested chart
      if (response.charts.length > 0) {
        onChartRequest(response.charts[0], inputValue);
      }
    }, 1500);
  };

  const generateResponse = (query: string): { content: string; charts: string[] } => {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('roi') || lowerQuery.includes('return')) {
      return {
        content: 'Here\'s your program ROI analysis. The current ROI is 23.5%, showing strong performance. I can show you ROI trends, cost breakdowns, or comparative analysis.',
        charts: ['roi-trend', 'cost-breakdown', 'roi-comparison']
      };
    }
    
    if (lowerQuery.includes('underperforming') || lowerQuery.includes('campaign')) {
      return {
        content: 'I\'ve identified the top 5 underperforming campaigns this month. The main issues are low engagement rates and high cost per acquisition. Here are optimization suggestions.',
        charts: ['campaign-performance', 'engagement-analysis', 'optimization-recommendations']
      };
    }
    
    if (lowerQuery.includes('enrollment') || lowerQuery.includes('member')) {
      return {
        content: 'Current enrollment metrics show 12.3% enrollment rate with 68% active members. I can break this down by channel, demographics, or time periods.',
        charts: ['enrollment-trends', 'member-activity', 'demographic-breakdown']
      };
    }
    
    if (lowerQuery.includes('redemption') || lowerQuery.includes('points')) {
      return {
        content: 'Points redemption analysis shows 76% redemption rate with $2.1M in outstanding liability. Breakage rate is currently 8.2%.',
        charts: ['redemption-trends', 'liability-overview', 'breakage-analysis']
      };
    }

    return {
      content: 'I can help you analyze various aspects of your loyalty program including ROI, member engagement, campaign performance, and operational metrics. What specific area would you like to explore?',
      charts: ['overview-dashboard', 'kpi-summary']
    };
  };

  const handleChartClick = (chartType: string, query: string) => {
    onChartRequest(chartType, query);
  };

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          Analytics Assistant
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0">
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-4 pb-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex gap-2 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user' ? 'bg-blue-500' : 'bg-green-500'
                  }`}>
                    {message.type === 'user' ? 
                      <User className="h-4 w-4 text-white" /> : 
                      <Bot className="h-4 w-4 text-white" />
                    }
                  </div>
                  <div className={`rounded-lg p-3 ${
                    message.type === 'user' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    <p className="text-sm">{message.content}</p>
                    {message.suggestedCharts && message.suggestedCharts.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {message.suggestedCharts.map((chart, index) => (
                          <Badge 
                            key={index}
                            variant="secondary" 
                            className="cursor-pointer text-xs"
                            onClick={() => handleChartClick(chart, message.content)}
                          >
                            {chart.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
        <div className="border-t p-4">
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask about your program performance..."
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              disabled={isLoading}
            />
            <Button onClick={handleSendMessage} disabled={isLoading || !inputValue.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
